import { createContext, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { login, loginWithToken } from "@/services/clientAreaService";
import { AuthResponse } from "../types/Auth";

interface User {
  cli_codigo: string;
  cli_fantasia: string;
  token: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithToken: (token: string, cve_codigo: string) => Promise<void>;
  signOut: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const response: AuthResponse = await login(email, password);
      if (!response.user) {
        throw new Error("Credenciais inválidas");
      }

      const userData: User = {
        cli_codigo: response.user.cli_codigo,
        cli_fantasia: response.user.cli_fantasia,
        token: response.user.token,
        email: response.user.email || email,
      };

      setUser(userData);
      localStorage.setItem("user", JSON.stringify(userData));
    } catch {
      throw new Error("Credenciais inválidas");
    }
  }, []);

  const signInWithToken = useCallback(
    async (token: string, cve_codigo: string) => {
      try {
        const response: AuthResponse = await loginWithToken(token, cve_codigo);
        if (!response.user) {
          throw new Error("Token inválido");
        }

        const userData: User = {
          cli_codigo: response.user.cli_codigo,
          cli_fantasia: response.user.cli_fantasia,
          token: response.user.token,
          email: response.user.email,
        };

        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
      } catch {
        throw new Error("Token inválido");
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    setUser(null);
    localStorage.removeItem("user");
    navigate("/login");
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        signIn,
        signInWithToken,
        signOut,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
